import { Button, Checkbox, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  type Channel,
  type CheckState,
  type RowValues,
  enabledCount,
  rowChannels
} from "../../helpers/notification-settings-matrix.ts"
import { type SmsLock } from "../../sms/use-sms-verification.ts"
import type { NotificationSettingsItem } from "../../types/notification.types"

export interface MatrixSection {
  key: string
  heading?: React.ReactNode
  items: NotificationSettingsItem[]
  isCritical: boolean
}

export interface MatrixBulk {
  label: string
  states: Partial<Record<Channel, CheckState>>
  onChannel: (channel: Channel, next: boolean) => void
}

interface NotificationMatrixProps {
  sections: MatrixSection[]
  columns: Channel[]
  rows: RowValues
  smsLock: SmsLock
  disabled: boolean
  onToggle: (
    item: NotificationSettingsItem,
    channel: Channel,
    next: boolean
  ) => void
  // Header checkboxes act on one category; search results span several, so
  // they get plain headers instead.
  bulk?: MatrixBulk
  onSmsSetup?: () => void
}

const CELL = "px-3 text-start align-middle"
const HEADER_CELL = `${CELL} h-12 font-medium text-sm text-gray-700 bg-muted border-b border-border sticky top-[calc(var(--settings-sticky-h,0px)-var(--main-content-pad-y,0px))] max-md:top-0 z-[2]`
const NAME_CELL =
  "ps-4 pe-3 text-start align-middle font-normal text-gray-800 sticky left-0 z-[1] bg-white"

function ChannelHeader({
  channel,
  bulk,
  smsLock,
  disabled,
  onSmsSetup
}: {
  channel: Channel
  bulk?: MatrixBulk
  smsLock: SmsLock
  disabled: boolean
  onSmsSetup?: () => void
}) {
  const { t } = useTranslation()
  const label = t(`notifications.channels.${channel}`)
  const smsLocked = channel === "sms" && smsLock !== null
  const state = bulk?.states[channel]

  const text =
    channel === "in_app" ? (
      <Tooltip title={t("notifications.settings.inAppHint")}>
        <span
          tabIndex={0}
          className="underline decoration-dotted underline-offset-4 cursor-help rounded-sm focus-visible:outline-2 focus-visible:outline-primary"
        >
          {label}
        </span>
      </Tooltip>
    ) : (
      <span>{label}</span>
    )

  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      {bulk && state && (
        <Tooltip
          title={smsLocked ? t(`smsVerification.locked.${smsLock}`) : undefined}
        >
          <span className="inline-flex">
            <Checkbox
              checked={state === "checked"}
              indeterminate={state === "mixed"}
              // antd marks "some on" with a class only; assistive tech needs
              // the mixed state spelled out.
              aria-checked={state === "mixed" ? "mixed" : undefined}
              disabled={disabled || smsLocked}
              onChange={(e) => bulk.onChannel(channel, e.target.checked)}
              data-testid={`bulk-toggle-${channel}`}
              aria-label={t("notifications.settings.toggleChannel", {
                channel: label,
                group: bulk.label
              })}
            />
          </span>
        </Tooltip>
      )}
      {text}
      {channel === "sms" && onSmsSetup && (
        <Button
          type="link"
          size="small"
          className="px-1 h-auto"
          onClick={onSmsSetup}
        >
          {t("notifications.settings.setUp")}
        </Button>
      )}
    </span>
  )
}

function ChannelCell({
  item,
  channel,
  row,
  isCritical,
  smsLock,
  disabled,
  onToggle
}: {
  item: NotificationSettingsItem
  channel: Channel
  row: RowValues[number]
  isCritical: boolean
  smsLock: SmsLock
  disabled: boolean
  onToggle: NotificationMatrixProps["onToggle"]
}) {
  const { t } = useTranslation()
  const channels = rowChannels(item)

  if (!channels.includes(channel)) {
    return (
      <td
        className={CELL}
        data-testid={`no-${channel}-${item.notification_type}`}
      >
        <Tooltip title={t("notifications.settings.notAvailable")}>
          <span
            role="img"
            aria-label={t("notifications.settings.notAvailableFor", {
              channel: t(`notifications.channels.${channel}`),
              type: item.notification_type_name
            })}
            className="inline-block w-4 text-center text-gray-400"
          >
            —
          </span>
        </Tooltip>
      </td>
    )
  }

  const checked = row[channel] === true
  const criticalLocked =
    isCritical && checked && enabledCount(row, channels) === 1
  const smsLocked = channel === "sms" && smsLock !== null

  return (
    <td className={CELL}>
      <Tooltip
        title={
          smsLocked
            ? t(`smsVerification.locked.${smsLock}`)
            : criticalLocked
              ? t("notifications.settings.criticalLocked")
              : undefined
        }
      >
        <span className="inline-flex">
          <Checkbox
            checked={checked}
            disabled={disabled || criticalLocked || smsLocked}
            onChange={(e) => onToggle(item, channel, e.target.checked)}
            aria-label={t("notifications.settings.toggleRowChannel", {
              channel: t(`notifications.channels.${channel}`),
              type: item.notification_type_name
            })}
          />
        </span>
      </Tooltip>
    </td>
  )
}

export function NotificationMatrix({
  sections,
  columns,
  rows,
  smsLock,
  disabled,
  onToggle,
  bulk,
  onSmsSetup
}: NotificationMatrixProps) {
  const { t } = useTranslation()

  return (
    <div className="rounded-lg border border-border bg-white max-md:overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th
              scope="col"
              className={`${HEADER_CELL} ps-4 left-0 max-md:z-[3] min-w-44 md:w-[34%] rounded-tl-lg`}
            >
              {t("notifications.settings.notificationColumn")}
            </th>
            {columns.map((channel, index) => (
              <th
                key={channel}
                scope="col"
                className={`${HEADER_CELL} min-w-28 ${index === columns.length - 1 ? "rounded-tr-lg" : ""}`}
              >
                <ChannelHeader
                  channel={channel}
                  bulk={bulk}
                  smsLock={smsLock}
                  disabled={disabled}
                  onSmsSetup={onSmsSetup}
                />
              </th>
            ))}
          </tr>
        </thead>
        {sections.map((section) => (
          <tbody key={section.key || "rows"}>
            {section.heading && (
              <tr>
                <th
                  scope="colgroup"
                  colSpan={columns.length + 1}
                  className="ps-4 pe-3 pt-4 pb-1.5 text-start text-xs font-semibold uppercase tracking-wide text-gray-500 bg-white border-b border-border"
                >
                  <span className="sticky left-4">{section.heading}</span>
                </th>
              </tr>
            )}
            {section.items.map((item) => (
              <tr
                key={item.notification_type}
                className="h-12 [&>*]:border-b [&>*]:border-border last:[&>*]:border-b-0 hover:bg-gray-50/60"
                data-testid={`notification-row-${item.notification_type}`}
              >
                <th scope="row" className={NAME_CELL}>
                  {item.notification_type_name}
                </th>
                {columns.map((channel) => (
                  <ChannelCell
                    key={channel}
                    item={item}
                    channel={channel}
                    row={rows[item.notification_type]}
                    isCritical={section.isCritical}
                    smsLock={smsLock}
                    disabled={disabled}
                    onToggle={onToggle}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  )
}
